import AggrTypes from "../../../queries/technogroup-result-details/AggrTypes";
import {getFilterParams} from "../../../utils/FilterUtils";
import {normalizeAggregate} from "./asyncTableColumns";

// krótkie oznaczenie typu agregatu przy wartości (wiersze grup, stopka): "Σ 125,96", "śr.w. 95,9";
// klucz tłumaczenia albo {key, lowerCase} - istniejący klucz pisany wielką literą
export const AGGREGATE_SHORT = {
    [AggrTypes.SUM]: "aggregateShort.sum",
    [AggrTypes.AVERAGE]: {key: "avgShort", lowerCase: true},
    [AggrTypes.WEIGHTED_AVERAGE]: "aggregateShort.weightedAvg",
    [AggrTypes.MIN]: {key: "grid.min", lowerCase: true},
    [AggrTypes.MAX]: {key: "max", lowerCase: true},
    [AggrTypes.COUNT_DISTINCT]: "aggregateShort.countDistinct",
    [AggrTypes.MOST_COMMON]: "aggregateShort.mostCommon",
};

/**
 * Krótkie oznaczenie typu agregatu (AGGREGATE_SHORT); nieznany typ - sam typ.
 * @param {string} type typ agregatu
 * @param {function} t funkcja tłumacząca
 * @returns {string}
 */
export function aggregateShortLabel(type, t) {
    const entry = AGGREGATE_SHORT[type];
    if (!entry) return type;
    if (typeof entry === "string") return t(entry);
    const label = t(entry.key);
    return entry.lowerCase ? label.toLowerCase() : label;
}

/**
 * Klucz agregatu w odpowiedzi grup (`aggs`) - jak w podsumowaniu tabeli: pole + indeks agregatu tej kolumny.
 */
export const aggregationName = (field, index) => `${field}_${index}`;

// maksymalna liczba poziomów grupowania - 3, bo każdy poziom ma własny, coraz słabszy odcień zieleni
// (backend przyjmuje do 5)
export const MAX_GROUP_LEVELS = 3;

/**
 * Filtr w formacie API z filtrów nagłówków. Musi być identyczny we wszystkich wywołaniach grupowania
 * (grupy każdego poziomu i wiersze), bo grupy są liczone po filtrach.
 * @param {Object} filters {[field]: {type, value}}
 * @returns {Array}
 */
export function buildFilter(filters) {
    const result = [];
    for (const key in filters) {
        const row = filters[key];
        const params = getFilterParams(row.type, row.value);
        if (params) result.push({field: key, ...params});
    }
    return result;
}

/**
 * Agregacje wierszy grup - ten sam format co podsumowanie tabeli (stopka), wszystkie agregaty każdej kolumny.
 * `name` to pole kolumny; backend zwraca je w `aggs` jako `pole_0`, `pole_1`, … w kolejności agregatów kolumny.
 * @param {Array} leafHeaders widoczne kolumny z danymi
 * @returns {Array<{type: string, column: string, name: string, weightColumn?: string}>}
 */
export function buildAggregations(leafHeaders) {
    const result = [];
    const fields = new Set();
    for (const header of leafHeaders) {
        if (fields.has(header.field)) continue;
        const types = normalizeAggregate(header.aggregate);
        if (types.length === 0) continue;
        fields.add(header.field);
        for (const type of types) {
            result.push({
                type,
                column: header.aggregateOptions?.massKey || header.field,
                name: header.field,
                weightColumn: header.aggregateOptions?.countKey,
            });
        }
    }
    return result;
}

/**
 * Klucz cache dla ścieżki grupy.
 * @param {Array<{field: string, value: *}>} path
 * @returns {string}
 */
export function pathKey(path) {
    return JSON.stringify(path);
}

/**
 * Wartość agregatu lub klucza grupy do wyświetlenia. Liczby całkowite przychodzą jako string,
 * więc zamieniamy je na number (jak stopka); daty zostają jako ISO dla formattera kolumny.
 * @param {*} value
 * @param {Object} header
 * @returns {*}
 */
export function formatGroupValue(value, header) {
    if (value === null || value === undefined) return null;
    let tmp = value;
    if (typeof value !== "boolean" && value !== "" && !isNaN(+value)) {
        // średnie przychodzą z pełną precyzją DOUBLE - zaokrąglamy też przed formatterem kolumny
        tmp = +(+value).toFixed(2);
    }
    if (header?.valueFormatter) return header.valueFormatter(tmp);
    return tmp;
}
